import React, { useEffect, useRef, useState } from 'react';
import {
  Books,
  ChatCircleDots,
  FilmStrip,
  Robot,
  Lightning,
  Video,
  ChartLineUp,
  BookBookmark,
  Star,
  User,
  MapPin,
  MusicNote,
  Fire,
  Check,
  Microphone,
  Trophy,
} from '@phosphor-icons/react';

// ── Slide definitions ────────────────────────────────────────
const SLIDES = [
  { key: 'classes', label: 'Live-Kurs', icon: Video, tone: '#AB1C24' },
  { key: 'textbook', label: 'Lehrbuch', icon: BookBookmark, tone: '#E6007E' },
  { key: 'exercises', label: 'Übungen', icon: Books, tone: '#AB1C24' },
  { key: 'conversation', label: 'Community', icon: ChatCircleDots, tone: '#5B8C9A' },
  { key: 'miniseries', label: 'Miniserie', icon: FilmStrip, tone: '#C65D2B' },
  { key: 'ai', label: 'KI-Lehrerin', icon: Robot, tone: '#6B8E23' },
  { key: 'challenge', label: 'Duell', icon: Lightning, tone: '#8A5A2B' },
  { key: 'analysis', label: 'Analyse', icon: ChartLineUp, tone: '#4F6D7A' },
  { key: 'album', label: 'Album', icon: BookBookmark, tone: '#D4A017' },
];

const SLIDE_MS = 20000;

// ── Slide bodies (inline mockups) ────────────────────────────

const ExercisesSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-stone-50 to-white flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs px-2 py-1 rounded-full bg-[#AB1C24]/10 text-[#AB1C24] font-semibold">A2 — Grammatik</span>
        <span className="text-xs text-stone-500">Frage 3 / 10</span>
      </div>
      <p className="font-heading text-xl font-bold text-[#292524] mb-4">Yo ___ español todos los días.</p>
      <div className="space-y-2">
        {['hablo', 'hablas', 'habla', 'hablamos'].map((opt, i) => (
          <div key={i} className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${i === 0 ? 'border-green-400 bg-green-50 text-green-800' : 'border-stone-200 bg-white text-[#292524]'}`}>
            {i === 0 && <Check size={14} className="inline mr-2" weight="bold" />}
            {opt}
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 text-sm text-[#6B8E23] font-semibold">
        <Star size={16} weight="fill" className="text-amber-500" />+1 Punkt · Richtig!
      </div>
    </div>
  </div>
);

const ConversationSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#5B8C9A]/10 to-white flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-5 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs px-2 py-0.5 rounded-full bg-[#5B8C9A]/15 text-[#5B8C9A] font-semibold">Thema des Tages</span>
      </div>
      <p className="font-heading font-bold text-[#292524]">Mi comida favorita</p>
      <div className="flex gap-3 items-start">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex-shrink-0 text-white text-xs font-bold flex items-center justify-center">A</div>
        <div className="flex-1 bg-stone-50 rounded-xl p-3 text-sm">
          <p className="font-semibold text-[#292524] text-xs">Anna · A2</p>
          <p className="text-[#57534E]">Me encanta la paella de mi abuela — ¡es la mejor del mundo!</p>
        </div>
      </div>
      <div className="flex gap-3 items-start">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6B8E23] to-[#556b1f] flex-shrink-0 text-white text-xs font-bold flex items-center justify-center">T</div>
        <div className="flex-1 bg-[#5B8C9A]/10 rounded-xl p-3 text-sm">
          <p className="font-semibold text-[#292524] text-xs">Thomas · B1</p>
          <div className="flex items-center gap-2 mt-1">
            <Microphone size={14} className="text-[#AB1C24]" weight="fill" />
            <div className="flex-1 h-1.5 rounded-full bg-stone-200 overflow-hidden">
              <div className="h-full bg-[#5B8C9A] w-2/3" />
            </div>
            <span className="text-xs text-stone-500">0:24</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MiniseriesSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#C65D2B]/15 to-stone-100 flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-bold text-[#292524] text-lg">Miniserie: Madrid</p>
        <span className="text-xs text-stone-500">3 / 5 Folgen</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { t: 'Puerta del Sol', c: '#C65D2B', done: true },
          { t: 'El Retiro', c: '#6B8E23', done: true },
          { t: 'Gran Vía', c: '#5B8C9A', done: true },
          { t: 'Lavapiés', c: '#AB1C24', done: false },
        ].map((m, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden shadow-sm aspect-video" style={{ background: `linear-gradient(135deg, ${m.c}, ${m.c}BB)` }}>
            <div className="absolute inset-0 flex items-end p-2">
              <p className="text-white text-xs font-semibold drop-shadow">{m.t}</p>
            </div>
            {m.done && <div className="absolute top-2 right-2 bg-white/95 rounded-full p-1"><Check size={10} weight="bold" className="text-green-600" /></div>}
          </div>
        ))}
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-stone-200 overflow-hidden">
        <div className="h-full bg-[#C65D2B]" style={{ width: '60%' }} />
      </div>
    </div>
  </div>
);

const AITeacherSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#6B8E23]/10 to-white flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6B8E23] to-[#556b1f] flex items-center justify-center">
          <Robot size={22} className="text-white" weight="fill" />
        </div>
        <div>
          <p className="font-heading font-bold text-[#292524] text-sm">Alberto KI</p>
          <p className="text-xs text-[#6B8E23]">online · Niveau A2</p>
        </div>
      </div>
      <div className="bg-stone-100 rounded-xl rounded-tl-sm p-3 text-sm text-[#292524] max-w-[85%]">
        Hola Anna, ¿cómo te fue el fin de semana?
      </div>
      <div className="bg-[#AB1C24]/10 rounded-xl rounded-tr-sm p-3 text-sm text-[#292524] ml-auto max-w-[85%]">
        Fue muy bien. Estube en Sevilla.
      </div>
      <div className="bg-[#6B8E23]/10 border border-[#6B8E23]/30 rounded-xl p-3 text-xs">
        <p className="text-[#556b1f] font-semibold mb-1">Korrektur:</p>
        <p className="text-[#57534E]"><span className="line-through text-red-500">Estube</span> → <span className="font-bold text-[#292524]">Estuve</span> (Pretérito Indefinido — irregulär)</p>
      </div>
    </div>
  </div>
);

const ChallengeSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#8A5A2B]/15 to-white flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-6">
      <div className="text-center mb-4">
        <span className="text-xs uppercase tracking-widest text-[#8A5A2B] font-bold">Duell · Vokabeln</span>
        <p className="font-heading text-xl font-bold text-[#292524] mt-1">Anna vs. Thomas</p>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-center flex-1">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white font-bold text-2xl flex items-center justify-center shadow-md">A</div>
          <p className="text-sm font-semibold mt-2">Anna</p>
          <p className="text-2xl font-heading font-bold text-[#6B8E23]">8</p>
        </div>
        <div className="text-center">
          <Lightning size={36} weight="fill" className="text-[#8A5A2B] animate-pulse" />
          <p className="text-xs text-stone-500 mt-1">Runde 9/10</p>
        </div>
        <div className="text-center flex-1">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#5B8C9A] to-[#3d6b7a] text-white font-bold text-2xl flex items-center justify-center shadow-md">T</div>
          <p className="text-sm font-semibold mt-2">Thomas</p>
          <p className="text-2xl font-heading font-bold text-[#292524]">7</p>
        </div>
      </div>
      <div className="mt-4 bg-stone-50 rounded-xl p-3 text-center">
        <p className="text-xs text-stone-500 mb-1">¿Cómo se dice "book"?</p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {['libro', 'silla', 'mesa', 'casa'].map((o, i) => (
            <div key={i} className={`text-sm py-1.5 rounded-lg border ${i === 0 ? 'border-[#6B8E23] bg-[#6B8E23]/10 text-[#6B8E23] font-semibold' : 'border-stone-200 text-[#292524]'}`}>{o}</div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ClassesSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#AB1C24]/10 to-stone-50 flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-[#AB1C24] flex items-center justify-center">
          <Video size={22} className="text-white" weight="fill" />
        </div>
        <div>
          <p className="font-heading font-bold text-[#292524]">Live-Kurs — heute 19:00</p>
          <p className="text-xs text-[#AB1C24] font-semibold">· LIVE in 12 Minuten</p>
        </div>
      </div>
      <div className="aspect-video bg-stone-900 rounded-xl overflow-hidden relative mb-3">
        <div className="absolute inset-0 bg-gradient-to-br from-[#AB1C24]/60 to-stone-900 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
            <div className="w-0 h-0 border-l-[14px] border-l-[#AB1C24] border-y-[9px] border-y-transparent ml-1" />
          </div>
        </div>
        <div className="absolute bottom-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">● LIVE</div>
      </div>
      <p className="text-sm text-[#292524]"><span className="font-semibold">Thema:</span> Pretérito Indefinido — irregulare Verben</p>
      <p className="text-xs text-stone-500 mt-0.5">7 Teilnehmer · Niveau A2 / B1</p>
    </div>
  </div>
);

const AnalysisSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#4F6D7A]/10 to-white flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
      <div className="bg-[#4F6D7A] px-5 py-3 flex items-center gap-2">
        <ChartLineUp size={18} className="text-white" weight="fill" />
        <p className="text-white text-sm font-semibold">Deine 14-Tage-Analyse</p>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-xs text-stone-500">Hallo Anna! Hier dein Fortschritt:</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-stone-50 rounded-lg p-2">
            <p className="text-xl font-heading font-bold text-[#292524]">42</p>
            <p className="text-[10px] text-stone-500">Übungen</p>
          </div>
          <div className="bg-stone-50 rounded-lg p-2">
            <p className="text-xl font-heading font-bold text-[#6B8E23]">87%</p>
            <p className="text-[10px] text-stone-500">Genauigkeit</p>
          </div>
          <div className="bg-stone-50 rounded-lg p-2">
            <p className="text-xl font-heading font-bold text-orange-500">14<Fire size={13} weight="fill" className="inline ml-0.5" /></p>
            <p className="text-[10px] text-stone-500">Serie</p>
          </div>
        </div>
        <div className="bg-[#6B8E23]/10 border-l-4 border-[#6B8E23] p-3 rounded-r-lg">
          <p className="text-xs font-semibold text-[#556b1f] mb-1">Stärke</p>
          <p className="text-sm text-[#292524]">Großartige Fortschritte bei Verben in der Gegenwart.</p>
        </div>
        <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg">
          <p className="text-xs font-semibold text-amber-800 mb-1">Verbesserung</p>
          <p className="text-sm text-[#292524]">Übe die Vergangenheitsformen — 3 Fehler letzte Woche.</p>
        </div>
      </div>
    </div>
  </div>
);

const AlbumSlide = () => {
  const items = [
    { t: 'personality', name: 'Cervantes', c: '#AB1C24', lvl: 14, icon: User, unlocked: true },
    { t: 'city', name: 'Sevilla', c: '#C65D2B', lvl: 7, icon: MapPin, unlocked: true },
    { t: 'song', name: 'Bailando', c: '#D4A017', lvl: 12, icon: MusicNote, unlocked: true },
    { t: 'personality', name: 'Goya', c: '#6B8E23', lvl: 22, icon: User, unlocked: true },
    { t: 'city', name: 'Bilbao', c: '#5B8C9A', lvl: 18, icon: MapPin, unlocked: true },
    { t: 'song', name: 'Gracias a la vida', c: '#8A5A2B', lvl: 26, icon: MusicNote, unlocked: true },
    { t: 'personality', name: '???', c: '#78716C', lvl: 32, icon: User, unlocked: false },
    { t: 'city', name: '???', c: '#78716C', lvl: 35, icon: MapPin, unlocked: false },
  ];
  return (
    <div className="w-full h-full bg-gradient-to-br from-[#D4A017]/10 to-white flex items-center justify-center px-8 py-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy size={18} weight="fill" className="text-[#D4A017]" />
            <p className="font-heading font-bold text-[#292524]">Sammelalbum</p>
          </div>
          <span className="text-xs text-stone-500">18 / 102 freigeschaltet</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.name + it.lvl} className={`rounded-lg overflow-hidden border ${it.unlocked ? 'border-stone-200' : 'border-stone-200 opacity-60'}`}>
                <div className="h-14 flex items-center justify-center relative" style={it.unlocked ? { background: `linear-gradient(135deg, ${it.c}, ${it.c}CC)` } : { background: '#E7E5E4' }}>
                  <Icon size={20} className={it.unlocked ? 'text-white' : 'text-stone-400'} weight="fill" />
                  <span className="absolute top-0.5 left-0.5 bg-white/90 text-[9px] font-bold px-1 rounded">L{it.lvl}</span>
                </div>
                <p className={`text-[10px] font-semibold text-center py-1 truncate px-1 ${it.unlocked ? 'text-[#292524]' : 'text-stone-400'}`}>{it.name}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const TEXTBOOK_COVER = "https://customer-assets.emergentagent.com/job_habla-daily/artifacts/0qj1d00n_Con%20gusto%20A1.jpg";

const TextbookSlide = () => (
  <div className="w-full h-full bg-gradient-to-br from-[#FEF3F2] via-white to-[#E6007E]/10 flex items-center justify-center px-8 py-6">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-stone-200 p-5 flex gap-4 items-center">
      <div className="w-28 sm:w-32 flex-shrink-0 rounded-lg overflow-hidden border border-stone-200 shadow-sm">
        <img
          src={TEXTBOOK_COVER}
          alt="Con gusto nuevo A1 — Lehrbuch"
          className="w-full h-auto block"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="inline-block text-[10px] uppercase tracking-[0.15em] font-bold text-[#E6007E] mb-1">Lehrbuch</span>
        <p className="font-heading text-lg font-bold text-[#292524] leading-tight">Con gusto nuevo A1</p>
        <p className="text-xs text-[#78716C] mt-1">Klett · Kurs- und Übungsbuch mit Audios und Videos</p>
        <ul className="mt-3 space-y-1 text-xs text-[#57534E]">
          <li className="flex items-center gap-1.5"><Check size={12} weight="bold" className="text-[#6B8E23]" /> Offizielles Lehrmaterial</li>
          <li className="flex items-center gap-1.5"><Check size={12} weight="bold" className="text-[#6B8E23]" /> Integrierte Übungen</li>
          <li className="flex items-center gap-1.5"><Check size={12} weight="bold" className="text-[#6B8E23]" /> Audios & Videos</li>
        </ul>
      </div>
    </div>
  </div>
);

const SLIDE_BODY = {
  classes: ClassesSlide,
  textbook: TextbookSlide,
  exercises: ExercisesSlide,
  conversation: ConversationSlide,
  miniseries: MiniseriesSlide,
  ai: AITeacherSlide,
  challenge: ChallengeSlide,
  analysis: AnalysisSlide,
  album: AlbumSlide,
};

// ── Main component ──────────────────────────────────────────

export default function LandingDemoReel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const progressRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (paused) return;
    setProgress(0);
    const tickMs = 50;
    let elapsed = 0;
    const tick = setInterval(() => {
      elapsed += tickMs;
      setProgress(Math.min(100, (elapsed / SLIDE_MS) * 100));
    }, tickMs);
    const adv = setTimeout(() => setIndex((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => { clearInterval(tick); clearTimeout(adv); };
  }, [index, paused]);

  // Self-heal: if a slide somehow doesn't render content within 3s, reset to first slide.
  useEffect(() => {
    if (paused) return;
    const safety = setTimeout(() => {
      const node = document.querySelector('[data-testid="landing-video"] [data-reel-slide]');
      if (!node || (node.offsetHeight === 0 && index !== 0)) setIndex(0);
    }, 3000);
    return () => clearTimeout(safety);
  }, [index, paused]);

  const Active = SLIDE_BODY[SLIDES[index].key];
  const activeTone = SLIDES[index].tone;

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl shadow-stone-900/20 bg-white aspect-video border border-stone-200"
      data-testid="landing-video"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Active slide — `key={index}` remounts so reelFade animation restarts each slide */}
      <div key={index} data-reel-slide className="absolute inset-0 animate-reel-fade">
        <Active />
      </div>

      {/* Branding badge */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full pl-2 pr-4 py-1.5 flex items-center gap-2 shadow-lg pointer-events-none z-10">
        <img src="/brand/hca-mark-256.png" alt="" className="w-7 h-7 rounded-full" />
        <span className="text-sm font-semibold text-[#292524]">Habla</span>
      </div>

      {/* Current chapter label */}
      <div className="absolute top-4 right-4 bg-[#292524]/85 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none z-10" style={{ background: activeTone + 'EE' }}>
        {React.createElement(SLIDES[index].icon, { size: 14, weight: 'fill' })}
        <span className="font-semibold">{SLIDES[index].label}</span>
        <span className="opacity-60">· {String(index + 1).padStart(2, '0')}/08</span>
      </div>

      {/* Chapter dots + progress strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/95 to-transparent pt-5 pb-3 px-4 z-10">
        <div ref={progressRef} className="h-1 bg-stone-200 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-[#AB1C24] transition-[width] duration-75" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between gap-1.5 flex-wrap">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setIndex(i)}
              aria-label={s.label}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-all ${
                i === index ? 'bg-[#292524] text-white scale-105' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
              }`}
              data-testid={`reel-tab-${s.key}`}
            >
              {React.createElement(s.icon, { size: 11, weight: 'fill' })}
              <span className="hidden sm:inline">{s.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
